import { errorToast, uploadFileToBucket } from '@/utils';
import { Button, Flex, Switch } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  useGetEmployeeDetailsQuery,
  useUpdateEmployeeMutation,
  useUploadEmployeeProfilePhotoMutation,
} from '../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import { CirclePlusIcon, PencilEdit02Icon } from '@/icons';
import IMAGES from '../../../../assets/images';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import CustomImage from '../../../../components/CustomImage';
import CustomSegment from '../../../../components/CustomSegment';
import PageTitle from '../../../../components/PageTitle';
import WithLoader from '../../../../components/WithLoader';
import useAuth from '../../../../hooks/useAuth';
import useModal from '../../../../hooks/useModal';
import { useSegment } from '../../../../hooks/useSegment';
import DashboardLayout from '../../../../layouts/DashboardLayout';
import { getErrorMessage } from '../../../../lib/error';
import { toast } from '../../../../lib/toast';
import type { SegmentedType } from '../../../../types/global.types';
import EmpRequest from './_components/EmployeeDetails/EmpRequest';
import AdditionalInfo from './_components/EmployeeDetails/AdditionalInfo';
import Attachments from './_components/EmployeeDetails/Attachments';
import { useActiveCustomFields } from './_components/shared/custom-fields.helpers';
import JobInfo from './_components/EmployeeDetails/JobInfo';
import PayrollInfo from './_components/EmployeeDetails/PayrollInfo';
import PersonalInfo from './_components/EmployeeDetails/PersonalInfo';
import UserSpecificLogs from './_components/EmployeeDetails/UserSpecificLogs';

type SegmentType =
  | 'personal'
  | 'job'
  | 'access'
  | 'payroll'
  | 'additional'
  | 'attachments'
  | 'documents'
  | 'history'
  | 'performance'
  | 'emp-request'
  | 'audit-logs';

const EmployeeDetails = () => {
  const { id } = useParams();
  const { segment, setSegment } = useSegment<SegmentType>(
    'emp-details-segment',
    'personal',
  );
  const { open, handleOpen, handleClose } = useModal<undefined, 'CONFIRM'>();
  const { access_token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: employeeData, isLoading } = useGetEmployeeDetailsQuery(
    String(id),
    { skip: !id },
  );

  const [updateEmployee, { isLoading: updatingStatus, error: updateError }] =
    useUpdateEmployeeMutation();

  const [uploadProfilePhoto, { isLoading: uploadingPhoto }] =
    useUploadEmployeeProfilePhotoMutation();

  const [isUploading, setIsUploading] = useState(false);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const res = await uploadFileToBucket(file, access_token as string);
      if (res.success) {
        // Update employee profile photo with new URL
        await uploadProfilePhoto({
          id: String(id),
          data: {
            photo: res.url,
          },
        }).unwrap();

        toast('success', 'Profile picture uploaded successfully');
      } else {
        toast('error', getErrorMessage(res || 'Failed to upload.'));
      }
    } catch (error) {
      console.error('Upload Error:', error);
      toast('error', 'An error occurred during upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const employee = employeeData?.data?.data;
  const [isActive, setIsActive] = useState(false);
  const [nextStatus, setNextStatus] = useState<boolean | null>(null);

  useEffect(() => {
    if (employee?.status) {
      setIsActive(employee.status.toLowerCase() === 'active');
    }
  }, [employee?.status]);

  const handleConfirm = async () => {
    if (nextStatus === null) return;

    const newStatus = nextStatus ? 'active' : 'inactive';

    try {
      await updateEmployee({
        id: String(id),
        data: { status: newStatus },
      }).unwrap();

      setIsActive(nextStatus);
      toast('success', `Employee status updated to ${newStatus}`);
      handleClose();
    } catch (error) {
      errorToast(updateError, 'Failed to update employee status.');
    } finally {
      setNextStatus(null);
    }
  };

  const { fields: customFields } = useActiveCustomFields();
  const hasCustomFields = customFields.length > 0;

  const SEGMENT_CONFIG: SegmentedType<SegmentType>[] = [
    {
      value: 'personal',
      label: 'Personal Info',
      view: <PersonalInfo employee={employee} loading={isLoading} />,
    },
    {
      value: 'job',
      label: 'Job Info',
      view: <JobInfo employee={employee} loading={isLoading} />,
    },
    // {
    //   value: 'access',
    //   label: 'Access Configuration',
    //   view: (
    //     <div className="text-white p-6">
    //       Access Configuration coming soon...
    //     </div>
    //   ),
    // },
    {
      value: 'payroll',
      label: 'Payroll Info',
      view: <PayrollInfo employee={employee} loading={isLoading} />,
    },
    {
      value: 'attachments',
      label: 'Attachments',
      // The route id is what every employee endpoint keys on.
      view: <Attachments employeeId={id} />,
    },
    // Only companies that defined custom fields get this tab.
    ...(hasCustomFields
      ? [
          {
            value: 'additional' as SegmentType,
            label: 'Additional Information',
            view: <AdditionalInfo employee={employee} loading={isLoading} />,
          },
        ]
      : []),
    {
      value: 'audit-logs',
      label: 'Audit Logs',
      view: <UserSpecificLogs userId={employee?.user_id} />,
    },
    ...(employee?.photo_request_status?.toLowerCase() === 'pending'
      ? [
          {
            value: 'emp-request' as SegmentType,
            label: 'Employee Request',
            view: <EmpRequest employee={employee} />,
          },
        ]
      : []),
    // {
    //   value: 'documents',
    //   label: 'Documents',
    //   view: <div className="text-white p-6">Documents coming soon...</div>,
    // },
    // {
    //   value: 'history',
    //   label: 'History',
    //   view: <div className="text-white p-6">History coming soon...</div>,
    // },
  ];
  const currentSegment = SEGMENT_CONFIG.find((s) => s.value === segment);

  return (
    <>
      <DashboardLayout title="Employee">
        <Flex align="center" justify="space-between">
          <PageTitle
            title="Employee Profile"
            items={[
              { title: 'Dashboard', path: '/admin/employee/dashboard' },
              {
                title: 'Employee Directory',
                path: '/admin/employee/dashboard',
              },
              { title: 'Profile' },
            ]}
          />

          <Link to={`/admin/employee/dashboard/employee-details/${id}/manage`}>
            <Button size="large" className="btn-primary">
              Edit Profile
              <PencilEdit02Icon size={20} />
            </Button>
          </Link>
        </Flex>

        <WithLoader isLoading={isLoading} className="rounded-xl">
          <Flex
            justify="space-between"
            className="px-[35px] py-10 bg-theme-slate-800 rounded-xl mt-6 items-start xl:items-center"
          >
            <div className="flex flex-wrap justify-between items-center gap-5 w-[85%]">
              <Flex align="center" gap={24}>
                {/* <CustomImage
                  src={IMAGES.avatar}
                  size={95}
                  className="rounded-full"
                /> */}
                <div className="relative">
                  <WithLoader
                    isLoading={isUploading || updatingStatus || uploadingPhoto}
                    className="rounded-full"
                  >
                    <CustomImage
                      size={95}
                      className="rounded-full object-cover"
                      src={employee?.photo || IMAGES.avatar}
                    />
                  </WithLoader>
                  <button
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    className="absolute right-[7px] bottom-[7px] bg-theme-slate-900 rounded-full"
                  >
                    <CirclePlusIcon size={18} />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </div>
                <div className="space-y-3">
                  <div className="text-2xl text-white font-black">
                    {employee?.name || '--'}
                  </div>

                  <div className="scroll-pt-4">
                    <p className="text-lg text-white font-medium">
                      {employee?.designation?.name || '--'}
                    </p>
                    <p className="text-lg text-white font-medium">
                      {employee?.department?.name || '--'}
                    </p>
                  </div>
                </div>
              </Flex>

              <div className="space-y-4">
                <div className="flex  flex-wrap lg:flex-nowrap !justify-between  items-center gap-5">
                  <InfoItem label="ID:" value={employee?.employee_id || '--'} />
                  <InfoItem
                    label="Joined:"
                    value={
                      employee?.joining_date
                        ? new Date(employee.joining_date).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            },
                          )
                        : '--'
                    }
                  />
                </div>

                <div className="flex flex-wrap lg:flex-nowrap !justify-between  items-center gap-5">
                  <InfoItem
                    label="Type:"
                    value={
                      employee?.employee_type
                        ? employee.employee_type.charAt(0).toUpperCase() +
                          employee.employee_type.slice(1)
                        : '--'
                    }
                  />
                  <InfoItem
                    label="Assign to:"
                    value={employee?.manager ? employee.manager.name : '--'}
                  />
                </div>
              </div>

              {/* <Flex gap={30} align="center" className="">
                <div className="text-center">
                  <h6 className="text-[30px] text-white font-medium">10%</h6>
                  <span className="block text-white text-base">Leave</span>
                </div>
                <div className="text-center">
                  <h6 className="text-[30px] text-white font-medium">94%</h6>
                  <span className="block text-white text-base">Attendance</span>
                </div>
                <div className="text-center">
                  <h6 className="text-[30px] text-white font-medium">4.8</h6>
                  <span className="block text-white text-base">
                    Performance Rating
                  </span>
                </div>
              </Flex> */}
            </div>
            <div className="space-y-4">
              <p className="text-base text-white">Status</p>
              <Switch
                checked={isActive}
                onChange={(checked) => {
                  setNextStatus(checked); // store intended value
                  handleOpen({ type: 'CONFIRM' }); // open confirm modal
                }}
                loading={updatingStatus}
                className="custom-switch"
              />
            </div>
          </Flex>
        </WithLoader>

        <CustomSegment
          config={SEGMENT_CONFIG}
          selected={segment}
          onChange={setSegment}
          className="px-0"
        />
        <div className="mt-5">{currentSegment?.view}</div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          data={open.data}
          type={open.type}
          handleClose={handleClose}
          handleSubmit={handleConfirm}
          open={open.open && open.type === 'CONFIRM'}
          loading={updatingStatus}
          title="Are you sure?"
          subTitle="You want to change this status"
          buttonName="Confirm"
        />
      </DashboardLayout>
    </>
  );
};

export default EmployeeDetails;

const InfoItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="w-full">
      <h6 className="text-base text-white">{label}</h6>
      <h6 className="text-xl text-white w-[250px]">{value}</h6>
    </div>
  );
};
